/**
 * Overlap detection for a sample group's pins.
 *
 * A mobile save can write the same grid point twice, so two samples end up on
 * the same coordinate (or a few metres apart). These helpers group those pins
 * so the map can ring them and the list can flag them.
 *
 * Pure functions on purpose: no Angular, no Google Maps, so they are cheap to
 * unit test and can be reused if this ever moves to the maps module.
 */

export interface OverlapPin {
  /** Must identify a physical pin, not a sample: one pin can carry many samples. */
  id: number | string;
  lat: number;
  lng: number;
}

/** Pins closer together than this are treated as the same physical point. */
export const DEFAULT_OVERLAP_METRES = 12;

const EARTH_RADIUS_M = 6371008.8;
const toRad = (deg: number): number => (deg * Math.PI) / 180;

/** Great-circle distance in metres between two lat/lng pairs. */
export function metresBetween(a: OverlapPin, b: OverlapPin): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Group pins that sit within `thresholdMetres` of each other.
 * Only groups of 2 or more are returned; a pin on its own is not an overlap.
 *
 * Greedy single pass, O(n^2). A sample group is tens to low hundreds of pins,
 * so this stays well under a millisecond and avoids a spatial index.
 */
export function clusterOverlaps<T extends OverlapPin>(
  pins: T[],
  thresholdMetres: number = DEFAULT_OVERLAP_METRES
): T[][] {
  const clusters: T[][] = [];
  const claimed = new Set<number | string>();

  for (const pin of pins) {
    if (claimed.has(pin.id)) continue;
    const group = pins.filter(
      other => !claimed.has(other.id) && metresBetween(pin, other) <= thresholdMetres
    );
    group.forEach(member => claimed.add(member.id));
    if (group.length > 1) clusters.push(group);
  }

  return clusters;
}

/** Ids of every pin that shares its position with at least one other pin. */
export function overlappingIds<T extends OverlapPin>(
  pins: T[],
  thresholdMetres: number = DEFAULT_OVERLAP_METRES
): Set<number | string> {
  const ids = new Set<number | string>();
  clusterOverlaps(pins, thresholdMetres).forEach(group =>
    group.forEach(pin => ids.add(pin.id))
  );
  return ids;
}

/** Mean position of a cluster, used to centre the overlap ring. */
export function clusterCentre<T extends OverlapPin>(group: T[]): google.maps.LatLngLiteral {
  const lat = group.reduce((sum, pin) => sum + pin.lat, 0) / group.length;
  const lng = group.reduce((sum, pin) => sum + pin.lng, 0) / group.length;
  return { lat, lng };
}

/**
 * Offset for a pin inside a cluster, so an exact duplicate is not hidden
 * underneath its twin. Returns metres east/north from the cluster centre.
 */
export function spreadOffset(index: number, size: number, radiusMetres = 9): { east: number; north: number } {
  const angle = (index / size) * Math.PI * 2 - Math.PI / 2;
  return { east: Math.cos(angle) * radiusMetres, north: Math.sin(angle) * radiusMetres };
}

/** Shift a position by metres east/north. Good enough at field scale. */
export function offsetPosition(
  origin: google.maps.LatLngLiteral,
  east: number,
  north: number
): google.maps.LatLngLiteral {
  const dLat = (north / EARTH_RADIUS_M) * (180 / Math.PI);
  const dLng = (east / (EARTH_RADIUS_M * Math.cos(toRad(origin.lat)))) * (180 / Math.PI);
  return { lat: origin.lat + dLat, lng: origin.lng + dLng };
}
